import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Plus, Users, Edit, Trash2, UserCheck, Calendar, ExternalLink, UserPlus, Clock, DollarSign, FileText, Download, History, FileSpreadsheet, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import { getUser } from "../utils/authService";
import BackNavigation from "../components/BackNavigation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const WorkforceManagement = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [workforce, setWorkforce] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [dailyWageWorkers, setDailyWageWorkers] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [activeTab, setActiveTab] = useState("summary");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dailyWageDialogOpen, setDailyWageDialogOpen] = useState(false);
  const [workerHistoryDialogOpen, setWorkerHistoryDialogOpen] = useState(false);
  const [selectedWorkerHistory, setSelectedWorkerHistory] = useState(null);
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editingDailyWage, setEditingDailyWage] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Export state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportMonth, setExportMonth] = useState(new Date().getMonth() + 1);
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [exporting, setExporting] = useState(false);
  
  // Report date range
  const [reportStartDate, setReportStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1); // First day of current month
    return d.toISOString().split('T')[0];
  });
  const [reportEndDate, setReportEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [wageReport, setWageReport] = useState(null);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    skilled_count: "",
    unskilled_count: "",
    supervisors_count: "",
    productivity_remarks: ""
  });
  
  // Daily wage worker form
  const [dailyWageForm, setDailyWageForm] = useState({
    name: "",
    phone: "",
    skill_type: "Unskilled",
    daily_rate: "",
    id_proof: "",
    address: ""
  });
  
  const [attendanceList, setAttendanceList] = useState([]);

  const currentUser = getUser() || {};
  const isMasterAdmin = currentUser.role === "Master Admin";

  const fetchProject = useCallback(async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      console.error("Failed to fetch project");
    }
  }, [projectId]);

  const fetchWorkforce = useCallback(async () => {
    try {
      const response = await apiClient.get(`/workforce/${projectId}`);
      setWorkforce(response.data);
    } catch (error) {
      console.error("Failed to fetch workforce");
    }
  }, [projectId]);

  const fetchEmployees = useCallback(async () => {
    try {
      // Fetch employees assigned to this project or unassigned (available for all)
      const response = await apiClient.get(`/employees/project/${projectId}`);
      setEmployees(response.data);
    } catch (error) {
      console.error("Failed to fetch employees");
      // Fallback to all active employees if endpoint fails
      try {
        const fallback = await apiClient.get(`/employees/active`);
        setEmployees(fallback.data);
      } catch {
        setEmployees([]);
      }
    }
  }, [projectId]);

  const fetchDailyWageWorkers = useCallback(async () => {
    try {
      const response = await apiClient.get(`/daily-wage-workers/${projectId}`);
      setDailyWageWorkers(response.data);
    } catch (error) {
      console.error("Failed to fetch daily wage workers");
      setDailyWageWorkers([]);
    }
  }, [projectId]);

  const fetchAttendance = useCallback(async () => {
    try {
      const response = await apiClient.get(`/attendance/${projectId}?date=${selectedDate}`);
      setAttendance(response.data);
      
      // If we have saved attendance, merge it with the full employee list
      // This ensures we always show ALL employees, with saved data merged in
      if (employees.length > 0) {
        const savedAttendanceMap = {};
        (response.data || []).forEach(att => {
          savedAttendanceMap[att.employee_id] = att;
        });
        
        setAttendanceList(employees.map(emp => {
          const saved = savedAttendanceMap[emp.id];
          return {
            employee_id: emp.id,
            employee_code: emp.employee_code,
            employee_name: emp.name,
            category: emp.category,
            status: saved?.status || "Present",
            overtime_hours: saved?.overtime_hours || saved?.ot_hours || 0,
            remarks: saved?.remarks || "",
            is_saved: !!saved // Track if this attendance was already saved
          };
        }));
      }
    } catch (error) {
      console.error("Failed to fetch attendance");
    }
  }, [projectId, selectedDate, employees]);

  const fetchWageReport = useCallback(async () => {
    try {
      const response = await apiClient.get(`/daily-wage-workers/${projectId}/report?start_date=${reportStartDate}&end_date=${reportEndDate}`);
      setWageReport(response.data);
    } catch (error) {
      console.error("Failed to fetch wage report");
      setWageReport(null);
    }
  }, [projectId, reportStartDate, reportEndDate]);

  const fetchWorkerHistory = async (workerId) => {
    try {
      const response = await apiClient.get(`/daily-wage-workers/${workerId}/history`);
      setSelectedWorkerHistory(response.data);
      setWorkerHistoryDialogOpen(true);
    } catch (error) {
      toast.error("Failed to fetch worker history");
    }
  };

  useEffect(() => {
    fetchProject();
    fetchWorkforce();
    fetchEmployees();
    fetchDailyWageWorkers();
  }, [fetchProject, fetchWorkforce, fetchEmployees, fetchDailyWageWorkers]);

  useEffect(() => {
    if (activeTab === "attendance") {
      fetchAttendance();
    }
  }, [activeTab, fetchAttendance]);

  // Populate attendance list from employees when switching to attendance tab
  useEffect(() => {
    if (activeTab === "attendance" && employees.length > 0 && attendanceList.length === 0) {
      // Only initialize if no attendance list exists yet
      setAttendanceList(employees.map(emp => ({
        employee_id: emp.id,
        employee_code: emp.employee_code,
        employee_name: emp.name,
        category: emp.category,
        status: "Present",  // Default to Present
        overtime_hours: 0,
        remarks: ""
      })));
    }
  }, [activeTab, employees, attendanceList.length]);

  useEffect(() => {
    if (activeTab === "wagereport") {
      fetchWageReport();
    }
  }, [activeTab, fetchWageReport]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        skilled_count: parseInt(formData.skilled_count),
        unskilled_count: parseInt(formData.unskilled_count),
        supervisors_count: parseInt(formData.supervisors_count)
      };

      if (editingEntry) {
        await apiClient.put(`/workforce/${editingEntry.id}`, data);
        toast.success("Workforce entry updated");
      } else {
        await apiClient.post('/workforce', {
          ...data,
          project_id: projectId
        });
        toast.success("Workforce entry added");
      }
      
      setDialogOpen(false);
      setEditingEntry(null);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        skilled_count: "",
        unskilled_count: "",
        supervisors_count: "",
        productivity_remarks: ""
      });
      fetchWorkforce();
    } catch (error) {
      toast.error(`Failed to ${editingEntry ? 'update' : 'add'} workforce entry`);
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      date: entry.date,
      skilled_count: entry.skilled_count.toString(),
      unskilled_count: entry.unskilled_count.toString(),
      supervisors_count: entry.supervisors_count.toString(),
      productivity_remarks: entry.productivity_remarks || ""
    });
    setDialogOpen(true);
  };

  const handleDelete = async (entryId, entryDate) => {
    if (!window.confirm(`Are you sure you want to delete workforce entry for "${entryDate}"?`)) {
      return;
    }
    
    try {
      await apiClient.delete(`/workforce/${entryId}`);
      toast.success("Workforce entry deleted successfully");
      fetchWorkforce();
    } catch (error) {
      toast.error("Failed to delete workforce entry");
    }
  };

  const openNewEntryDialog = () => {
    setEditingEntry(null);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      skilled_count: "",
      unskilled_count: "",
      supervisors_count: "",
      productivity_remarks: ""
    });
    setDialogOpen(true);
  };

  const handleAttendanceChange = (empId, field, value) => {
    setAttendanceList(prev => prev.map(item => 
      item.employee_id === empId ? { ...item, [field]: value } : item
    ));
  };

  const saveAttendance = async () => {
    try {
      const response = await apiClient.post(`/attendance/bulk?project_id=${projectId}&date=${selectedDate}`, attendanceList);
      
      // Show appropriate message based on response
      if (response.data.skipped > 0) {
        toast.warning(`${response.data.recorded} new records saved. ${response.data.skipped} existing records were not modified (only Master Admin can edit saved attendance).`);
      } else if (response.data.updated > 0) {
        toast.success(`Attendance saved: ${response.data.recorded} new, ${response.data.updated} updated`);
      } else {
        toast.success("Attendance saved successfully");
      }
      fetchAttendance();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Only Master Admin can modify saved attendance records");
      } else {
        toast.error("Failed to save attendance");
      }
    }
  };
  
  // Export attendance report
  const handleExportAttendance = async (format = "excel") => {
    setExporting(true);
    try {
      const response = await apiClient.get(
        `/attendance/report/${projectId}/export?month=${exportMonth}&year=${exportYear}&format=${format}`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance_report_${exportYear}_${String(exportMonth).padStart(2, '0')}.${format === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Report downloaded successfully");
      setExportDialogOpen(false);
    } catch (error) {
      toast.error("Failed to export report");
    } finally {
      setExporting(false);
    }
  };

  // Daily Wage Worker handlers
  const resetDailyWageForm = () => {
    setDailyWageForm({
      name: "",
      phone: "",
      skill_type: "Unskilled",
      daily_rate: "",
      id_proof: "",
      address: ""
    });
    setEditingDailyWage(null);
  };

  const handleDailyWageSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...dailyWageForm,
        daily_rate: parseFloat(dailyWageForm.daily_rate) || 0,
        project_id: projectId
      };

      if (editingDailyWage) {
        await apiClient.put(`/daily-wage-workers/${editingDailyWage.id}`, data);
        toast.success("Daily wage worker updated");
      } else {
        await apiClient.post('/daily-wage-workers', data);
        toast.success("Daily wage worker added");
      }
      
      setDailyWageDialogOpen(false);
      resetDailyWageForm();
      fetchDailyWageWorkers();
    } catch (error) {
      toast.error(`Failed to ${editingDailyWage ? 'update' : 'add'} daily wage worker`);
    }
  };

  const handleEditDailyWage = (worker) => {
    setEditingDailyWage(worker);
    setDailyWageForm({
      name: worker.name,
      phone: worker.phone || "",
      skill_type: worker.skill_type,
      daily_rate: worker.daily_rate?.toString() || "",
      id_proof: worker.id_proof || "",
      address: worker.address || ""
    });
    setDailyWageDialogOpen(true);
  };

  const handleDeleteDailyWage = async (workerId, workerName) => {
    if (!window.confirm(`Are you sure you want to remove "${workerName}" from daily wage workers?`)) {
      return;
    }
    
    try {
      await apiClient.delete(`/daily-wage-workers/${workerId}`);
      toast.success("Daily wage worker removed");
      fetchDailyWageWorkers();
    } catch (error) {
      toast.error("Failed to remove daily wage worker");
    }
  };

  const markDailyWagePresent = async (workerId, date, isPresent) => {
    try {
      await apiClient.post(`/daily-wage-workers/${workerId}/attendance`, {
        date: date,
        is_present: isPresent,
        project_id: projectId
      });
      toast.success(isPresent ? "Marked present" : "Marked absent");
      fetchDailyWageWorkers();
    } catch (error) {
      toast.error("Failed to update attendance");
    }
  };

  const totalWorkforce = workforce.reduce((sum, w) => sum + w.total_count, 0);
  const avgWorkforce = workforce.length > 0 ? totalWorkforce / workforce.length : 0;
  
  const presentCount = attendanceList.filter(a => a.status === "Present").length;
  const absentCount = attendanceList.filter(a => a.status === "Absent").length;

  return (
    <div>
      <BackNavigation title="Workforce Management" projectName={project?.name} />
      
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">WORKFORCE MANAGEMENT</h1>
          <p className="text-slate-600 mt-2 font-body">Track daily attendance and productivity</p>
        </div>
        <Link to="/hr" className="btn-outline flex items-center space-x-2" data-testid="go-to-hr-button">
          <Users className="w-4 h-4" />
          <span>HR Module</span>
          <ExternalLink className="w-3 h-3" />
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b border-slate-200">
        <button
          onClick={() => setActiveTab("summary")}
          data-testid="tab-summary"
          className={`pb-3 px-4 font-medium text-sm transition-colors ${
            activeTab === "summary" 
              ? "border-b-2 border-primary text-primary" 
              : "text-slate-600 hover:text-primary"
          }`}
        >
          <Users className="w-4 h-4 inline mr-2" />
          Daily Summary
        </button>
        <button
          onClick={() => setActiveTab("attendance")}
          data-testid="tab-attendance"
          className={`pb-3 px-4 font-medium text-sm transition-colors ${
            activeTab === "attendance" 
              ? "border-b-2 border-primary text-primary" 
              : "text-slate-600 hover:text-primary"
          }`}
        >
          <UserCheck className="w-4 h-4 inline mr-2" />
          Employee Attendance ({employees.length})
        </button>
        <button
          onClick={() => setActiveTab("dailywage")}
          data-testid="tab-dailywage"
          className={`pb-3 px-4 font-medium text-sm transition-colors ${
            activeTab === "dailywage" 
              ? "border-b-2 border-primary text-primary" 
              : "text-slate-600 hover:text-primary"
          }`}
        >
          <DollarSign className="w-4 h-4 inline mr-2" />
          Daily Wage Workers ({dailyWageWorkers.length})
        </button>
        <button
          onClick={() => setActiveTab("wagereport")}
          data-testid="tab-wagereport"
          className={`pb-3 px-4 font-medium text-sm transition-colors ${
            activeTab === "wagereport" 
              ? "border-b-2 border-primary text-primary" 
              : "text-slate-600 hover:text-primary"
          }`}
        >
          <FileText className="w-4 h-4 inline mr-2" />
          Wage Report
        </button>
      </div>

      {activeTab === "summary" && (
        <>
          <div className="flex justify-end mb-4">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <button onClick={openNewEntryDialog} data-testid="add-workforce-button" className="btn-primary flex items-center space-x-2">
                  <Plus className="w-4 h-4" />
                  <span>Add Entry</span>
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">{editingEntry ? 'EDIT WORKFORCE ENTRY' : 'ADD WORKFORCE ENTRY'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Date</label>
                    <input
                      type="date"
                      data-testid="workforce-date-input"
                      required
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Skilled</label>
                      <input
                        type="number"
                        data-testid="workforce-skilled-input"
                        required
                        value={formData.skilled_count}
                        onChange={(e) => setFormData({ ...formData, skilled_count: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Unskilled</label>
                      <input
                        type="number"
                        data-testid="workforce-unskilled-input"
                        required
                        value={formData.unskilled_count}
                        onChange={(e) => setFormData({ ...formData, unskilled_count: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Supervisors</label>
                      <input
                        type="number"
                        data-testid="workforce-supervisors-input"
                        required
                        value={formData.supervisors_count}
                        onChange={(e) => setFormData({ ...formData, supervisors_count: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Productivity Remarks</label>
                    <textarea
                      data-testid="workforce-remarks-input"
                      value={formData.productivity_remarks}
                      onChange={(e) => setFormData({ ...formData, productivity_remarks: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      rows="3"
                    />
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button type="button" onClick={() => {
                      setDialogOpen(false);
                      setEditingEntry(null);
                    }} className="btn-outline">Cancel</button>
                    <button type="submit" data-testid="submit-workforce-button" className="btn-primary">
                      {editingEntry ? 'Update Entry' : 'Add Entry'}
                    </button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <div className="stat-card">
              <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Average Daily Workforce</p>
              <p className="font-heading font-black text-4xl text-primary font-mono" data-testid="avg-workforce-count">{Math.round(avgWorkforce)}</p>
            </div>
            <div className="stat-card">
              <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Entries</p>
              <p className="font-heading font-black text-4xl text-primary font-mono" data-testid="total-entries">{workforce.length}</p>
            </div>
          </div>

          <div className="stat-card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Date</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Skilled</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Unskilled</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Supervisors</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Total</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Remarks</th>
                  {isMasterAdmin && (
                    <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {workforce.map((entry) => (
                  <tr key={entry.id} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`workforce-row-${entry.id}`}>
                    <td className="py-3 px-4 font-mono text-sm font-medium">{entry.date}</td>
                    <td className="py-3 px-4 text-right font-mono text-sm">{entry.skilled_count}</td>
                    <td className="py-3 px-4 text-right font-mono text-sm">{entry.unskilled_count}</td>
                    <td className="py-3 px-4 text-right font-mono text-sm">{entry.supervisors_count}</td>
                    <td className="py-3 px-4 text-right font-mono text-sm font-bold text-primary">{entry.total_count}</td>
                    <td className="py-3 px-4 text-sm">{entry.productivity_remarks || '-'}</td>
                    {isMasterAdmin && (
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => handleEdit(entry)}
                            data-testid={`edit-workforce-${entry.id}`}
                            className="p-1 hover:bg-slate-200 rounded"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4 text-primary" />
                          </button>
                          <button
                            onClick={() => handleDelete(entry.id, entry.date)}
                            data-testid={`delete-workforce-${entry.id}`}
                            className="p-1 hover:bg-red-100 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-error" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {workforce.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Users className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                No workforce entries yet.
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === "attendance" && (
        <>
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Select Date</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  data-testid="attendance-date-input"
                  className="px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                />
              </div>
              <div className="pt-6">
                <span className="text-sm text-slate-600">
                  Present: <span className="font-bold text-success">{presentCount}</span> | 
                  Absent: <span className="font-bold text-error">{absentCount}</span> | 
                  Total: <span className="font-bold">{attendanceList.length}</span>
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setExportDialogOpen(true)}
                className="btn-outline flex items-center space-x-2"
                data-testid="export-attendance-btn"
              >
                <FileSpreadsheet className="w-4 h-4" />
                <span>Export Report</span>
              </button>
              <button
                onClick={saveAttendance}
                data-testid="save-attendance-button"
                className="btn-primary flex items-center space-x-2"
              >
                <UserCheck className="w-4 h-4" />
                <span>Save Attendance</span>
              </button>
            </div>
          </div>

          {/* Notice for non-Master Admin users about editing saved records */}
          {!isMasterAdmin && attendanceList.some(att => att.is_saved) && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                <span className="font-semibold">Note:</span> Saved attendance records (highlighted rows) can only be modified by the Master Admin. You can still add attendance for employees who don't have saved records for this date.
              </p>
            </div>
          )}

          {employees.length === 0 ? (
            <div className="stat-card text-center py-12">
              <Users className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              <h3 className="font-heading font-bold text-xl mb-2">No Employees Found</h3>
              <p className="text-slate-600 mb-4">Add employees in the HR module to track attendance.</p>
              <Link to="/hr" className="btn-primary inline-flex items-center space-x-2">
                <Users className="w-4 h-4" />
                <span>Go to HR Module</span>
              </Link>
            </div>
          ) : (
            <div className="stat-card overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-slate-300">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Code</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Name</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Category</th>
                    <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Status</th>
                    <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">OT Hours</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceList.map((att) => {
                    const isLocked = !isMasterAdmin && att.is_saved;
                    return (
                    <tr 
                      key={att.employee_id} 
                      className={`border-b border-slate-200 hover:bg-slate-50 ${isLocked ? 'bg-slate-50' : ''}`}
                      data-testid={`attendance-row-${att.employee_id}`}
                    >
                      <td className="py-3 px-4 font-mono text-sm font-medium text-primary">
                        {att.employee_code}
                        {isLocked && (
                          <span className="ml-2 text-amber-500" title="Saved - Only Master Admin can edit">
                            🔒
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm font-medium">{att.employee_name}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-sm text-xs font-medium ${
                          att.category === 'Skilled' ? 'bg-primary/10 text-primary' :
                          att.category === 'Semi-skilled' ? 'bg-accent/10 text-accent' :
                          att.category === 'Supervisor' ? 'bg-success/10 text-success' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {att.category}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <select
                          value={att.status}
                          onChange={(e) => handleAttendanceChange(att.employee_id, 'status', e.target.value)}
                          disabled={isLocked}
                          className={`px-3 py-1 rounded-sm text-sm font-medium border-0 focus:ring-2 focus:ring-primary ${
                            att.status === 'Present' ? 'bg-success/20 text-success' :
                            att.status === 'Absent' ? 'bg-error/20 text-error' :
                            att.status === 'Half Day' ? 'bg-warning/20 text-warning' :
                            'bg-slate-100 text-slate-600'
                          } ${isLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                        >
                          <option value="Present">Present</option>
                          <option value="Absent">Absent</option>
                          <option value="Half Day">Half Day</option>
                          <option value="On Leave">On Leave</option>
                        </select>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          value={att.overtime_hours}
                          onChange={(e) => handleAttendanceChange(att.employee_id, 'overtime_hours', parseFloat(e.target.value) || 0)}
                          disabled={isLocked}
                          className={`w-20 px-2 py-1 border border-slate-300 rounded-sm text-center font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary ${isLocked ? 'bg-slate-100 opacity-60 cursor-not-allowed' : ''}`}
                        />
                      </td>
                      <td className="py-3 px-4">
                        <input
                          type="text"
                          value={att.remarks}
                          onChange={(e) => handleAttendanceChange(att.employee_id, 'remarks', e.target.value)}
                          disabled={isLocked}
                          placeholder="Optional remarks"
                          className={`w-full px-2 py-1 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary ${isLocked ? 'bg-slate-100 opacity-60 cursor-not-allowed' : ''}`}
                        />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Daily Wage Workers Tab */}
      {activeTab === "dailywage" && (
        <>
          <div className="flex justify-between items-center mb-6">
            <div>
              <p className="text-sm text-slate-600">
                Track temporary workers hired on a daily basis. These are not permanent employees.
              </p>
            </div>
            <Dialog open={dailyWageDialogOpen} onOpenChange={(open) => {
              setDailyWageDialogOpen(open);
              if (!open) resetDailyWageForm();
            }}>
              <DialogTrigger asChild>
                <button 
                  onClick={() => {
                    resetDailyWageForm();
                    setDailyWageDialogOpen(true);
                  }}
                  data-testid="add-daily-wage-button" 
                  className="btn-primary flex items-center space-x-2"
                >
                  <UserPlus className="w-4 h-4" />
                  <span>Add Daily Wage Worker</span>
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">
                    {editingDailyWage ? 'EDIT DAILY WAGE WORKER' : 'ADD DAILY WAGE WORKER'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleDailyWageSubmit} className="space-y-4 mt-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Name *</label>
                    <input
                      type="text"
                      required
                      value={dailyWageForm.name}
                      onChange={(e) => setDailyWageForm({ ...dailyWageForm, name: e.target.value })}
                      data-testid="daily-wage-name"
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Worker name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Phone</label>
                      <input
                        type="tel"
                        value={dailyWageForm.phone}
                        onChange={(e) => setDailyWageForm({ ...dailyWageForm, phone: e.target.value })}
                        data-testid="daily-wage-phone"
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                        placeholder="9876543210"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Skill Type *</label>
                      <select
                        required
                        value={dailyWageForm.skill_type}
                        onChange={(e) => setDailyWageForm({ ...dailyWageForm, skill_type: e.target.value })}
                        data-testid="daily-wage-skill"
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="Unskilled">Unskilled</option>
                        <option value="Semi-skilled">Semi-skilled</option>
                        <option value="Skilled">Skilled</option>
                        <option value="Helper">Helper</option>
                        <option value="Mason">Mason</option>
                        <option value="Carpenter">Carpenter</option>
                        <option value="Welder">Welder</option>
                        <option value="Fitter">Fitter</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Daily Rate (₹) *</label>
                      <input
                        type="number"
                        required
                        min="0"
                        value={dailyWageForm.daily_rate}
                        onChange={(e) => setDailyWageForm({ ...dailyWageForm, daily_rate: e.target.value })}
                        data-testid="daily-wage-rate"
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                        placeholder="500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">ID Proof (Aadhar/Other)</label>
                      <input
                        type="text"
                        value={dailyWageForm.id_proof}
                        onChange={(e) => setDailyWageForm({ ...dailyWageForm, id_proof: e.target.value })}
                        data-testid="daily-wage-id"
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                        placeholder="XXXX XXXX XXXX"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Address</label>
                    <textarea
                      value={dailyWageForm.address}
                      onChange={(e) => setDailyWageForm({ ...dailyWageForm, address: e.target.value })}
                      data-testid="daily-wage-address"
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      rows="2"
                      placeholder="Village/Town, District"
                    />
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button type="button" onClick={() => setDailyWageDialogOpen(false)} className="btn-outline">Cancel</button>
                    <button type="submit" data-testid="submit-daily-wage" className="btn-primary">
                      {editingDailyWage ? 'Update Worker' : 'Add Worker'}
                    </button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Daily Wage Summary Stats */}
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <div className="stat-card">
              <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Workers</p>
              <p className="font-heading font-black text-3xl text-primary font-mono">{dailyWageWorkers.length}</p>
            </div>
            <div className="stat-card">
              <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Skilled</p>
              <p className="font-heading font-black text-3xl text-success font-mono">
                {dailyWageWorkers.filter(w => w.skill_type === 'Skilled').length}
              </p>
            </div>
            <div className="stat-card">
              <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Unskilled</p>
              <p className="font-heading font-black text-3xl text-warning font-mono">
                {dailyWageWorkers.filter(w => w.skill_type === 'Unskilled').length}
              </p>
            </div>
            <div className="stat-card">
              <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Today's Cost</p>
              <p className="font-heading font-black text-3xl text-accent font-mono">
                ₹{dailyWageWorkers.reduce((sum, w) => sum + (w.daily_rate || 0), 0).toLocaleString('en-IN')}
              </p>
            </div>
          </div>

          {/* Daily Wage Workers List */}
          <div className="stat-card overflow-x-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-heading font-bold text-lg">Daily Wage Workers for {selectedDate}</h3>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-600 mr-2">Date:</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="px-3 py-1 border border-slate-300 rounded-sm font-mono text-sm"
                />
              </div>
            </div>
            
            {dailyWageWorkers.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <DollarSign className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                <h3 className="font-heading font-bold text-xl mb-2">No Daily Wage Workers</h3>
                <p className="text-slate-600 mb-4">Add temporary workers who are hired on a daily basis.</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-slate-300">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Name</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Phone</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Skill Type</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Daily Rate</th>
                    <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Present Today</th>
                    <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Days Worked</th>
                    <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyWageWorkers.map((worker) => (
                    <tr key={worker.id} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`daily-wage-row-${worker.id}`}>
                      <td className="py-3 px-4 font-medium">{worker.name}</td>
                      <td className="py-3 px-4 font-mono text-sm">{worker.phone || '-'}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-sm text-xs font-medium ${
                          worker.skill_type === 'Skilled' ? 'bg-primary/10 text-primary' :
                          worker.skill_type === 'Semi-skilled' ? 'bg-accent/10 text-accent' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {worker.skill_type}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-sm font-bold">₹{worker.daily_rate}</td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => markDailyWagePresent(worker.id, selectedDate, !worker.attendance?.[selectedDate])}
                          className={`px-3 py-1 rounded-sm text-xs font-medium transition-colors ${
                            worker.attendance?.[selectedDate] 
                              ? 'bg-success/20 text-success' 
                              : 'bg-slate-100 text-slate-600 hover:bg-success/10'
                          }`}
                        >
                          {worker.attendance?.[selectedDate] ? '✓ Present' : 'Mark Present'}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-center font-mono text-sm">
                        {worker.total_days_worked || 0}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => fetchWorkerHistory(worker.id)}
                            className="p-1 hover:bg-blue-100 rounded"
                            title="View History"
                          >
                            <History className="w-4 h-4 text-blue-600" />
                          </button>
                          <button
                            onClick={() => handleEditDailyWage(worker)}
                            className="p-1 hover:bg-slate-200 rounded"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4 text-primary" />
                          </button>
                          <button
                            onClick={() => handleDeleteDailyWage(worker.id, worker.name)}
                            className="p-1 hover:bg-red-100 rounded"
                            title="Remove"
                          >
                            <Trash2 className="w-4 h-4 text-error" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Wage Report Tab */}
      {activeTab === "wagereport" && (
        <>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="font-heading font-bold text-lg">Daily Wage Payment Report</h3>
              <p className="text-sm text-slate-600">Generate weekly/monthly wage summaries for payment processing.</p>
            </div>
            <div className="flex items-center space-x-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-600 mr-2">From:</label>
                <input
                  type="date"
                  value={reportStartDate}
                  onChange={(e) => setReportStartDate(e.target.value)}
                  className="px-3 py-1 border border-slate-300 rounded-sm font-mono text-sm"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-600 mr-2">To:</label>
                <input
                  type="date"
                  value={reportEndDate}
                  onChange={(e) => setReportEndDate(e.target.value)}
                  className="px-3 py-1 border border-slate-300 rounded-sm font-mono text-sm"
                />
              </div>
              <button
                onClick={fetchWageReport}
                className="btn-primary flex items-center space-x-2"
              >
                <FileText className="w-4 h-4" />
                <span>Generate Report</span>
              </button>
            </div>
          </div>

          {/* Report Summary Cards */}
          {wageReport && (
            <>
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                <div className="stat-card bg-primary/5 border-l-4 border-l-primary">
                  <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Workers</p>
                  <p className="font-heading font-black text-3xl text-primary font-mono">{wageReport.total_workers}</p>
                </div>
                <div className="stat-card bg-success/5 border-l-4 border-l-success">
                  <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Man-Days</p>
                  <p className="font-heading font-black text-3xl text-success font-mono">{wageReport.total_days}</p>
                </div>
                <div className="stat-card bg-accent/5 border-l-4 border-l-accent">
                  <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Wages Due</p>
                  <p className="font-heading font-black text-3xl text-accent font-mono">₹{wageReport.total_cost?.toLocaleString('en-IN')}</p>
                </div>
                <div className="stat-card bg-slate-100 border-l-4 border-l-slate-400">
                  <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Report Period</p>
                  <p className="font-mono text-sm">{reportStartDate} to {reportEndDate}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {Math.ceil((new Date(reportEndDate) - new Date(reportStartDate)) / (1000 * 60 * 60 * 24)) + 1} days
                  </p>
                </div>
              </div>

              {/* Detailed Report Table */}
              <div className="stat-card overflow-x-auto">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-heading font-bold text-lg">Worker-wise Payment Summary</h4>
                  <button
                    onClick={() => {
                      // Export to CSV
                      const headers = ['Name', 'Skill Type', 'Daily Rate', 'Days Present', 'Wages Due'];
                      const rows = wageReport.workers.map(w => [w.name, w.skill_type, w.daily_rate, w.days_present, w.wages_due]);
                      const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `wage_report_${reportStartDate}_to_${reportEndDate}.csv`;
                      a.click();
                    }}
                    className="btn-outline flex items-center space-x-2 text-sm"
                  >
                    <Download className="w-4 h-4" />
                    <span>Export CSV</span>
                  </button>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-slate-300">
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Name</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Skill Type</th>
                      <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Daily Rate (₹)</th>
                      <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Days Present</th>
                      <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Wages Due (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wageReport.workers?.map((worker) => (
                      <tr key={worker.id} className="border-b border-slate-200 hover:bg-slate-50">
                        <td className="py-3 px-4 font-medium">{worker.name}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-sm text-xs font-medium ${
                            worker.skill_type === 'Skilled' ? 'bg-primary/10 text-primary' :
                            worker.skill_type === 'Semi-skilled' ? 'bg-accent/10 text-accent' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {worker.skill_type}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-sm">₹{worker.daily_rate}</td>
                        <td className="py-3 px-4 text-right font-mono text-sm font-bold text-primary">{worker.days_present}</td>
                        <td className="py-3 px-4 text-right font-mono text-sm font-bold text-success">₹{worker.wages_due?.toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50">
                      <td colSpan="3" className="py-3 px-4 font-bold">TOTAL</td>
                      <td className="py-3 px-4 text-right font-mono font-bold text-primary">{wageReport.total_days}</td>
                      <td className="py-3 px-4 text-right font-mono font-bold text-success text-lg">₹{wageReport.total_cost?.toLocaleString('en-IN')}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}

          {!wageReport && (
            <div className="stat-card text-center py-12">
              <FileText className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              <h3 className="font-heading font-bold text-xl mb-2">Generate Wage Report</h3>
              <p className="text-slate-600 mb-4">Select date range and click "Generate Report" to view payment summary.</p>
            </div>
          )}
        </>
      )}

      {/* Worker History Dialog */}
      <Dialog open={workerHistoryDialogOpen} onOpenChange={setWorkerHistoryDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              WORKER PROJECT HISTORY
              {selectedWorkerHistory && (
                <span className="text-base font-normal text-slate-600 ml-2">
                  - {selectedWorkerHistory.worker_name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedWorkerHistory && (
            <div className="space-y-4 mt-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-primary/5 rounded-sm">
                  <p className="text-xs uppercase tracking-widest text-slate-600">Total Projects</p>
                  <p className="font-heading font-bold text-2xl text-primary">{selectedWorkerHistory.total_projects}</p>
                </div>
                <div className="p-3 bg-success/5 rounded-sm">
                  <p className="text-xs uppercase tracking-widest text-slate-600">Total Days Worked</p>
                  <p className="font-heading font-bold text-2xl text-success">{selectedWorkerHistory.total_days}</p>
                </div>
                <div className="p-3 bg-accent/5 rounded-sm">
                  <p className="text-xs uppercase tracking-widest text-slate-600">Total Earnings</p>
                  <p className="font-heading font-bold text-2xl text-accent">₹{selectedWorkerHistory.total_earnings?.toLocaleString('en-IN')}</p>
                </div>
              </div>

              {/* Project-wise History */}
              <div className="border rounded-sm">
                <div className="bg-slate-50 px-4 py-2 border-b">
                  <h4 className="font-heading font-bold text-sm uppercase tracking-widest">Project History</h4>
                </div>
                <div className="divide-y">
                  {selectedWorkerHistory.projects?.map((proj) => (
                    <div key={proj.project_id || `${proj.project_name}-${proj.first_day}`} className="p-4 hover:bg-slate-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-primary">{proj.project_name}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {proj.first_day} to {proj.last_day}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-bold">{proj.days_worked} days</p>
                          <p className="text-sm text-success font-mono">₹{proj.earnings?.toLocaleString('en-IN')}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {selectedWorkerHistory.projects?.length === 0 && (
                    <div className="p-8 text-center text-slate-500">
                      <History className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      <p>No project history available.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Export Attendance Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">EXPORT ATTENDANCE REPORT</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Export monthly attendance report for all employees assigned to this project.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Month</label>
                <select
                  value={exportMonth}
                  onChange={(e) => setExportMonth(parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {[
                    "January", "February", "March", "April", "May", "June",
                    "July", "August", "September", "October", "November", "December"
                  ].map((month, idx) => (
                    <option key={idx} value={idx + 1}>{month}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Year</label>
                <select
                  value={exportYear}
                  onChange={(e) => setExportYear(parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {[2024, 2025, 2026, 2027].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button onClick={() => setExportDialogOpen(false)} className="btn-outline">
                Cancel
              </button>
              <button
                onClick={() => handleExportAttendance('csv')}
                disabled={exporting}
                data-testid="export-csv-btn"
                className="btn-outline flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>CSV</span>
              </button>
              <button
                onClick={() => handleExportAttendance('excel')}
                disabled={exporting}
                data-testid="export-excel-btn"
                className="btn-primary flex items-center space-x-2"
              >
                <FileSpreadsheet className="w-4 h-4" />
                <span>{exporting ? 'Exporting...' : 'Excel'}</span>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkforceManagement;
